import { useChainCallback } from '@/composables/functional/useChainCallback'
import type { LGraphNode } from '@/lib/litegraph/src/litegraph'
import { resolveNodeRootGraphId } from '@/lib/litegraph/src/litegraph'
import { defineDeprecatedProperty } from '@/lib/litegraph/src/utils/feedback'
import type { InputSpec } from '@/schemas/nodeDef/nodeDefSchemaV2'
import { app } from '@/scripts/app'
import type { ComfyWidgetConstructorV2 } from '@/scripts/widgets'
import { useWidgetValueStore } from '@/stores/widgetValueStore'
import { widgetId } from '@/types/widgetId'

function createPasswordInputElement(
  value: string,
  placeholder: string
): HTMLInputElement {
  const element = document.createElement('input')
  element.type = 'password'
  element.className = 'comfy-password-input'
  element.dataset.testid = 'dom-widget-password'
  element.value = value
  element.placeholder = placeholder
  element.style.width = '100%'
  element.style.boxSizing = 'border-box'
  return element
}

function bindPasswordInputWidget(
  widget: ReturnType<LGraphNode['addDOMWidget']>,
  element: HTMLInputElement
): void {
  const controller = new AbortController()
  const { signal } = controller

  element.addEventListener(
    'input',
    () => {
      widget.value = element.value
      widget.callback?.(widget.value)
    },
    { signal }
  )

  widget.onRemove = useChainCallback(widget.onRemove, () => {
    controller.abort()
  })
}

export const usePasswordWidget = () => {
  const widgetConstructor: ComfyWidgetConstructorV2 = (
    node: LGraphNode,
    inputSpec: InputSpec
  ) => {
    const widgetStore = useWidgetValueStore()
    const defaultVal = (inputSpec.default as string) ?? ''
    const inputEl = createPasswordInputElement(
      defaultVal,
      inputSpec.placeholder ?? inputSpec.name
    )

    const widget = node.addDOMWidget(inputSpec.name, 'password', inputEl, {
      getValue(): string {
        const graphId = resolveNodeRootGraphId(node, app.rootGraph.id)
        const widgetState = widgetStore.getWidget(
          widgetId(graphId, node.id, inputSpec.name)
        )

        return (widgetState?.value as string) ?? inputEl.value
      },
      setValue(v: string) {
        inputEl.value = v
        const graphId = resolveNodeRootGraphId(node, app.rootGraph.id)
        const widgetState = widgetStore.getWidget(
          widgetId(graphId, node.id, inputSpec.name)
        )
        if (widgetState) widgetState.value = v
      }
    })

    widget.element = inputEl
    defineDeprecatedProperty(
      widget,
      'inputEl',
      'element',
      'widget.inputEl is deprecated. Use widget.element instead.'
    )
    widget.options.minNodeSize = [200, 40]

    bindPasswordInputWidget(widget, inputEl)

    return widget
  }

  return widgetConstructor
}
